/**
 * Audio Upload Block
 * Dynamic slot management, file validation, audio preview, image gallery selection,
 * default audio pre-population, and song name input (three-way mutual exclusivity).
 * Files and image URLs are collected by product-form.js on form submission.
 */
(function () {
  'use strict';

  var MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB Shopify limit
  var ACCEPTED_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/wave', 'audio/x-wav', 'audio/ogg', 'audio/vorbis', 'audio/m4a', 'audio/x-m4a', 'audio/aac', 'audio/flac', 'audio/x-flac', 'audio/mp4', 'audio/webm'];
  var ACCEPTED_EXTENSIONS = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.webm'];

  /* ── Helpers ──────────────────────────────────────────── */

  function isAudioFile(file) {
    if (file.type && ACCEPTED_TYPES.indexOf(file.type) !== -1) {
      return true;
    }
    var fileName = file.name.toLowerCase();
    for (var i = 0; i < ACCEPTED_EXTENSIONS.length; i++) {
      var ext = ACCEPTED_EXTENSIONS[i];
      if (fileName.indexOf(ext, fileName.length - ext.length) !== -1) {
        return true;
      }
    }
    return false;
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function showError(slot, message) {
    slot.classList.add('audio-upload-slot--error');
    var errorEl = slot.querySelector('.audio-upload-slot__error');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'audio-upload-slot__error';
      slot.appendChild(errorEl);
    }
    errorEl.textContent = message;
  }

  function clearError(slot) {
    slot.classList.remove('audio-upload-slot--error');
    var errorEl = slot.querySelector('.audio-upload-slot__error');
    if (errorEl) {
      errorEl.remove();
    }
  }

  function getSlots(block) {
    return block.querySelectorAll('.audio-upload-slot');
  }

  function getMaxSlots(block) {
    return parseInt(block.getAttribute('data-max-slots'), 10) || 10;
  }

  /* ── Default audio handling ────────────────────────────── */

  function clearDefaultAudio(slot) {
    var defaultInput = slot.querySelector('[data-slot-default-audio]');
    if (defaultInput) {
      defaultInput.value = '';
    }
  }

  function setDefaultAudio(slot, audioUrl) {
    var defaultInput = slot.querySelector('[data-slot-default-audio]');
    if (defaultInput) {
      defaultInput.value = audioUrl;
    }

    // Show audio player with default audio
    var previewEl = slot.querySelector('.audio-upload-slot__preview');
    var playerEl = slot.querySelector('.audio-upload-slot__player');
    var removeBtn = slot.querySelector('.audio-upload-slot__btn--remove');
    var filenameEl = slot.querySelector('.audio-upload-slot__filename');

    // Extract filename from URL
    var audioName = audioUrl.split('/').pop().split('?')[0] || 'default audio';

    if (filenameEl) {
      filenameEl.textContent = audioName + ' (default)';
      filenameEl.classList.add('audio-upload-slot__filename--has-file');
    }
    if (removeBtn) removeBtn.hidden = false;
    if (playerEl) playerEl.src = audioUrl;
    if (previewEl) previewEl.hidden = false;

    // Clear any uploaded file (mutual exclusivity)
    var fileInput = slot.querySelector('.audio-upload-slot__input');
    if (fileInput) fileInput.value = '';

    // Disable song name input
    var songInput = slot.querySelector('.audio-upload-slot__text-input');
    if (songInput) {
      songInput.value = '';
      songInput.disabled = true;
    }
  }

  /* ── Audio file handling ──────────────────────────────── */

  function handleFileSelect(slot, file) {
    // Clear default audio FIRST, before any validation
    clearDefaultAudio(slot);

    clearError(slot);

    if (!isAudioFile(file)) {
      showError(slot, 'Please select an audio file (MP3, WAV, OGG, M4A, AAC, FLAC).');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showError(slot, 'File is too large. Maximum size is 20MB. Your file is ' + formatFileSize(file.size) + '.');
      return;
    }

    var filenameEl = slot.querySelector('.audio-upload-slot__filename');
    filenameEl.textContent = file.name + ' (' + formatFileSize(file.size) + ')';
    filenameEl.classList.add('audio-upload-slot__filename--has-file');

    var removeBtn = slot.querySelector('.audio-upload-slot__btn--remove');
    removeBtn.hidden = false;

    var previewEl = slot.querySelector('.audio-upload-slot__preview');
    var playerEl = slot.querySelector('.audio-upload-slot__player');
    var previewUrl = URL.createObjectURL(file);
    playerEl.src = previewUrl;
    previewEl.hidden = false;

    slot._previewUrl = previewUrl;

    // Disable song name input (mutual exclusivity)
    var songInput = slot.querySelector('.audio-upload-slot__text-input');
    if (songInput) {
      songInput.value = '';
      songInput.disabled = true;
    }
  }

  function clearAudioFile(slot) {
    var input = slot.querySelector('.audio-upload-slot__input');
    input.value = '';

    var filenameEl = slot.querySelector('.audio-upload-slot__filename');
    filenameEl.textContent = 'No file chosen';
    filenameEl.classList.remove('audio-upload-slot__filename--has-file');

    var removeBtn = slot.querySelector('.audio-upload-slot__btn--remove');
    removeBtn.hidden = true;

    var previewEl = slot.querySelector('.audio-upload-slot__preview');
    var playerEl = slot.querySelector('.audio-upload-slot__player');
    playerEl.pause();
    playerEl.src = '';
    previewEl.hidden = true;

    if (slot._previewUrl) {
      URL.revokeObjectURL(slot._previewUrl);
      slot._previewUrl = null;
    }

    // Clear default audio
    clearDefaultAudio(slot);

    clearError(slot);

    // Re-enable song name input
    var songInput = slot.querySelector('.audio-upload-slot__text-input');
    if (songInput) {
      songInput.disabled = false;
    }
  }

  /* ── Song name handling (three-way mutual exclusivity) ── */

  function handleSongInput(slot) {
    var songInput = slot.querySelector('.audio-upload-slot__text-input');
    var controls = slot.querySelector('.audio-upload-slot__controls');

    if (songInput && songInput.value.trim()) {
      // Text entered: disable file controls, clear audio
      if (controls) controls.classList.add('audio-upload-slot__controls--disabled');
      // Clear any uploaded file or default audio
      clearAudioFile(slot);
      // Re-disable song input since clearAudioFile re-enables it
      songInput.disabled = false;
    } else {
      // Text cleared: re-enable file controls
      if (controls) controls.classList.remove('audio-upload-slot__controls--disabled');
    }
  }

  /* ── Gallery image handling ────────────────────────────── */

  var activeSlotIndex = null;

  function openGallery(block, slotIndex) {
    activeSlotIndex = slotIndex;
    var gallery = block._gallery;
    if (!gallery) return;
    gallery.hidden = false;
  }

  function closeGallery(block) {
    var gallery = block._gallery;
    if (gallery) {
      gallery.hidden = true;
    }
    activeSlotIndex = null;
  }

  function selectImage(block, imageUrl, thumbUrl, audioUrl) {
    if (activeSlotIndex === null) return;

    var slot = block.querySelector('.audio-upload-slot[data-slot-index="' + activeSlotIndex + '"]');
    if (!slot) return;

    // Set image
    var imageArea = slot.querySelector('.audio-upload-slot__image-area');
    var placeholder = slot.querySelector('.audio-upload-slot__image-placeholder');
    var image = slot.querySelector('.audio-upload-slot__image');
    var hiddenInput = slot.querySelector('[data-slot-image-input]');

    if (placeholder) placeholder.hidden = true;
    if (image) {
      image.src = thumbUrl;
      image.hidden = false;
    }
    if (imageArea) imageArea.classList.add('audio-upload-slot__image-area--selected');
    if (hiddenInput) hiddenInput.value = imageUrl;

    // If gallery item has audio, set it as default
    if (audioUrl) {
      setDefaultAudio(slot, audioUrl);
    }

    closeGallery(block);
  }

  /* ── Slot creation / removal / renumbering ────────────── */

  function createSlot(block, index) {
    var template = block.querySelector('template[data-slot-template]');
    if (!template) return null;

    var html = template.innerHTML.replace(/__INDEX__/g, String(index));
    var wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    var slot = wrapper.firstElementChild;

    var container = block.querySelector('[data-slots-container]');
    container.appendChild(slot);

    initSlot(block, slot);
    return slot;
  }

  function populateSlot(slot, thumbUrl, imageUrl, audioUrl) {
    // Set image
    var imageArea = slot.querySelector('.audio-upload-slot__image-area');
    var placeholder = slot.querySelector('.audio-upload-slot__image-placeholder');
    var image = slot.querySelector('.audio-upload-slot__image');
    var imageInput = slot.querySelector('[data-slot-image-input]');

    if (placeholder) placeholder.hidden = true;
    if (image) {
      image.src = thumbUrl;
      image.hidden = false;
    }
    if (imageArea) imageArea.classList.add('audio-upload-slot__image-area--selected');
    if (imageInput) imageInput.value = imageUrl;

    // Set default audio
    if (audioUrl) {
      setDefaultAudio(slot, audioUrl);
    }
  }

  function addSlot(block) {
    var slots = getSlots(block);
    if (slots.length >= getMaxSlots(block)) return;

    var nextIndex = slots.length + 1;
    createSlot(block, nextIndex);
    updateAddButtonState(block);
  }

  function removeSlot(block, slot) {
    if (slot._previewUrl) {
      URL.revokeObjectURL(slot._previewUrl);
    }

    slot.remove();
    renumberSlots(block);
    updateAddButtonState(block);
  }

  function renumberSlots(block) {
    var slots = getSlots(block);
    slots.forEach(function (slot, index) {
      var newIndex = index + 1;

      slot.setAttribute('data-slot-index', String(newIndex));

      var label = slot.querySelector('.audio-upload-slot__label');
      if (label) label.textContent = 'Card ' + newIndex;

      var fileInput = slot.querySelector('.audio-upload-slot__input');
      if (fileInput) {
        fileInput.setAttribute('name', 'properties[Card ' + newIndex + ']');
        fileInput.setAttribute('data-slot', String(newIndex));
      }

      var hiddenInput = slot.querySelector('[data-slot-image-input]');
      if (hiddenInput) {
        hiddenInput.setAttribute('name', 'properties[Card ' + newIndex + ' Image]');
        hiddenInput.setAttribute('data-slot-image-input', String(newIndex));
      }

      var defaultAudioInput = slot.querySelector('[data-slot-default-audio]');
      if (defaultAudioInput) {
        defaultAudioInput.setAttribute('name', 'properties[Card ' + newIndex + ' Default Audio]');
        defaultAudioInput.setAttribute('data-slot-default-audio', String(newIndex));
      }

      var imageArea = slot.querySelector('[data-slot-image-trigger]');
      if (imageArea) {
        imageArea.setAttribute('data-slot-image-trigger', String(newIndex));
      }

      var image = slot.querySelector('.audio-upload-slot__image');
      if (image) {
        image.setAttribute('alt', 'Card ' + newIndex + ' image');
      }

      var songInput = slot.querySelector('[data-slot-song]');
      if (songInput) {
        songInput.setAttribute('name', 'properties[Card ' + newIndex + ' Song]');
        songInput.setAttribute('data-slot-song', String(newIndex));
      }
    });
  }

  function updateAddButtonState(block) {
    var addBtn = block.querySelector('[data-add-slot]');
    if (!addBtn) return;

    var slots = getSlots(block);
    var maxSlots = getMaxSlots(block);

    if (slots.length >= maxSlots) {
      addBtn.disabled = true;
      addBtn.textContent = 'Maximum ' + maxSlots + ' slots reached';
    } else {
      addBtn.disabled = false;
      addBtn.textContent = 'Add Card';
    }

    updatePriceHint(block);
  }

  /* ── Price hint ────────────────────────────────────────── */

  function updatePriceHint(block) {
    var priceHint = block.querySelector('[data-price-hint]');
    if (!priceHint) return;

    var baseCount = parseInt(block.getAttribute('data-base-card-count'), 10) || 5;
    var extraPrice = parseFloat(block.getAttribute('data-extra-card-price')) || 0;
    var slotCount = getSlots(block).length;
    var extraCount = Math.max(0, slotCount - baseCount);

    // Store for product-form.js
    block._extraCardCount = extraCount;
    block._extraCardVariantId = block.getAttribute('data-extra-card-variant-id');

    if (extraCount > 0) {
      var extraCharge = extraCount * extraPrice;
      priceHint.textContent = baseCount + ' cards included. ' + extraCount + ' extra card' + (extraCount > 1 ? 's' : '') + ' at $' + extraPrice.toFixed(2) + ' each — charged as Extra Card (+$' + extraCharge.toFixed(2) + ')';
      priceHint.hidden = false;
    } else {
      priceHint.hidden = true;
    }
  }

  /* ── Slot initialization ──────────────────────────────── */

  function initSlot(block, slot) {
    var input = slot.querySelector('.audio-upload-slot__input');
    var selectBtn = slot.querySelector('.audio-upload-slot__btn--select');
    var removeBtn = slot.querySelector('.audio-upload-slot__btn--remove');
    var deleteSlotBtn = slot.querySelector('.audio-upload-slot__btn--delete-slot');
    var imageArea = slot.querySelector('.audio-upload-slot__image-area');
    var songInput = slot.querySelector('.audio-upload-slot__text-input');
    var slotIndex = slot.getAttribute('data-slot-index');

    // Upload File button -> trigger file input
    if (selectBtn) {
      selectBtn.addEventListener('click', function () {
        input.click();
      });
    }

    if (input) {
      input.addEventListener('change', function () {
        if (input.files && input.files[0]) {
          handleFileSelect(slot, input.files[0]);
        } else {
          clearAudioFile(slot);
        }
      });
    }

    // Remove audio (file or default) - keep slot
    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        clearAudioFile(slot);
      });
    }

    // Delete entire slot
    if (deleteSlotBtn) {
      deleteSlotBtn.addEventListener('click', function () {
        removeSlot(block, slot);
      });
    }

    // Image gallery selection
    if (imageArea) {
      imageArea.addEventListener('click', function () {
        openGallery(block, slotIndex);
      });
    }

    // Song name input (three-way mutual exclusivity)
    if (songInput) {
      songInput.addEventListener('input', function () {
        handleSongInput(slot);
      });
    }
  }

  function initGallery(block) {
    var gallery = block._gallery;
    if (!gallery) return;

    var closeButtons = gallery.querySelectorAll('[data-gallery-close]');
    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        closeGallery(block);
      });
    });

    var items = gallery.querySelectorAll('.audio-upload-gallery__item');
    items.forEach(function (item) {
      item.addEventListener('click', function () {
        var imageUrl = item.getAttribute('data-gallery-image');
        var thumbUrl = item.getAttribute('data-gallery-thumb') || imageUrl;
        var audioUrl = item.getAttribute('data-gallery-audio') || '';
        selectImage(block, imageUrl, thumbUrl, audioUrl);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !gallery.hidden) {
        closeGallery(block);
      }
    });
  }

  /* ── Block initialization ──────────────────────────────── */

  function initBlock(block) {
    if (block._audioUploadInit) return;
    block._audioUploadInit = true;

    // Move gallery to <body> to avoid CSS transform containment issues
    var gallery = block.querySelector('[data-audio-gallery]');
    if (gallery) {
      document.body.appendChild(gallery);
      block._gallery = gallery;
    }

    // Read gallery items that have audio URLs (for pre-populated slots)
    var galleryItems = block._gallery ? block._gallery.querySelectorAll('.audio-upload-gallery__item') : [];
    var audioPairs = [];
    galleryItems.forEach(function (item) {
      var imageUrl = item.getAttribute('data-gallery-image');
      var thumbUrl = item.getAttribute('data-gallery-thumb') || imageUrl;
      var audioUrl = item.getAttribute('data-gallery-audio');
      if (audioUrl) {
        audioPairs.push({ image: imageUrl, thumb: thumbUrl, audio: audioUrl });
      }
    });

    // Create pre-populated slots from gallery items with audio, or 1 empty slot
    if (audioPairs.length > 0) {
      audioPairs.forEach(function (pair, index) {
        var slot = createSlot(block, index + 1);
        if (slot) {
          populateSlot(slot, pair.thumb, pair.image, pair.audio);
        }
      });
    } else {
      createSlot(block, 1);
    }

    var addBtn = block.querySelector('[data-add-slot]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        addSlot(block);
      });
    }

    initGallery(block);
    updateAddButtonState(block);
  }

  function init() {
    var blocks = document.querySelectorAll('[data-audio-upload]');
    blocks.forEach(function (block) {
      initBlock(block);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var blocks = event.target.querySelectorAll('[data-audio-upload]');
    blocks.forEach(function (block) {
      initBlock(block);
    });
  });
})();
